import {
  Dispatch, SetStateAction, useCallback, useState,
} from 'react';
import { upperFirst } from 'lodash';
import {
  useSnackbar,
  useTable,
  useTableFetch,
  useTranslations,
  useTableLocalization,
  useTablePreserverActivePageValidator,
  useTablePreserverDecoder,
  useTablePreserverEncoder,
  usePubSub,
  buildTablePreserverDecoderConfigItem,
  TablePreserverCommonInitialTableValues,
  IUseTableReturn,
  IUseTableResetTableMethodsReturn,
  IUseTableFetchReturn,
  IUseTableLocalizationReturn,
  TUseTablePreserverDecoderConfigs,
  TablePreserverDecoderConfigItemTypeEnum,
  TSetSearchParams,
  IUseFilterButtonReturn,
  useFilterButton,
} from '@uniqkey-frontend/shared-app';
import {
  GetPartnerUsersResponsePartnerUser,
  PartnerUserRole,
  PartnerUsersOrderProperty,
} from '@uniqkey-backend-partner/api-client';
import { useSearchParams } from 'react-router-dom';
import PubSubEventEnum from '../../../enums/PubSubEventEnum';
import { IPartnerUsersTableRow } from '../../../components/tables/PartnerUsersTable';
import {
  IGetPartnerUsersParams,
  TGetPartnerUsersMethod,
} from '../../usePartnerUsersAPI/interfaces';
import usePartnerUsersAPI from '../../usePartnerUsersAPI';
import {
  IPartnerUsersListFilterSubmitResult,
} from '../../../pages/PartnerUsersPage/components/PartnerUsersListFilter';

interface IPersistentFilters extends Pick<
  IGetPartnerUsersParams, 'partnerId'
> {}

export interface IUsePartnerUsersTableProps {
  persistentFilters?: IPersistentFilters;
  noDataMessageKey: string;
}

export interface IUsePartnerUsersTableReturn extends
  Omit<IUseTableReturn<IPartnerUsersTableRow>, 'selectedRows' | 'resetTableMethods'>,
  Pick<IUseTableResetTableMethodsReturn, 'resetActivePage' | 'resetSelectedRows'>,
  Omit<IUseTableFetchReturn<GetPartnerUsersResponsePartnerUser>, 'data'>,
  IUseFilterButtonReturn
{
  partnerUsers: IUseTableFetchReturn<GetPartnerUsersResponsePartnerUser>['data'];
  selectedPartnerUsers: IUseTableReturn<IPartnerUsersTableRow>['selectedRows'];
  localization: IUseTableLocalizationReturn['localization'];
  searchQuery: IGetPartnerUsersParams['searchQuery'];
  setSearchQuery: Dispatch<SetStateAction<IGetPartnerUsersParams['searchQuery']>>;
  filterValues: IPartnerUsersListFilterSubmitResult;
  setFilterValues: Dispatch<SetStateAction<IPartnerUsersListFilterSubmitResult>>
}

interface ITablePreservationConfigs extends
  Omit<IGetPartnerUsersParams,
    'page' | 'pageLength' | 'partnerId' | 'orderPropertyName' | 'isDescending' | 'cancelToken'
  >,
  Pick<IUseTableReturn<IPartnerUsersTableRow>, 'columnOrderBy' | 'columnOrderDirection'>
{
  activePage: IGetPartnerUsersParams['page'];
  perPage: IGetPartnerUsersParams['pageLength'];
}

const PRESERVATION_CONFIGS: TUseTablePreserverDecoderConfigs<ITablePreservationConfigs> = {
  activePage: true,
  perPage: true,
  searchQuery: true,
  columnOrderBy: buildTablePreserverDecoderConfigItem(
    TablePreserverDecoderConfigItemTypeEnum.ENUM,
    { enumToCheck: PartnerUsersOrderProperty },
  ),
  columnOrderDirection: true,
  roleFilter: buildTablePreserverDecoderConfigItem(
    TablePreserverDecoderConfigItemTypeEnum.ENUM,
    { enumToCheck: PartnerUserRole },
  ),
};

const usePartnerUsersTable = (
  props: IUsePartnerUsersTableProps,
): IUsePartnerUsersTableReturn => {
  const {
    persistentFilters,
    noDataMessageKey,
  } = props;

  const { t } = useTranslations();
  const { showError } = useSnackbar();
  const { getPartnerUsers } = usePartnerUsersAPI();
  const [searchParams, setSearchParams] = useSearchParams();

  const handleOnRequestError = useCallback(() => {
    showError({ text: t('common.somethingWentWrong') });
  }, [showError, t]);

  const {
    activePage: initialActivePage,
    perPage: initialPerPage,
    searchQuery: initialSearchQuery,
    roleFilter: initialRoleFilter,
    columnOrderBy: initialColumnOrderBy,
    columnOrderDirection: initialColumnOrderDirection,
  } = useTablePreserverDecoder<ITablePreservationConfigs>(searchParams, PRESERVATION_CONFIGS);

  const [searchQuery, setSearchQuery] = useState<
    IGetPartnerUsersParams['searchQuery']
  >(() => initialSearchQuery);
  const [filterValues, setFilterValues] = useState<
    IPartnerUsersListFilterSubmitResult
  >({
    roleFilter: initialRoleFilter,
  });

  const { isFilterActive, numberOfActiveFilters } = useFilterButton(filterValues);

  const {
    activePage,
    perPage,
    columnOrderBy,
    columnOrderDirection,
    selectedRows: selectedPartnerUsers,
    resetTableMethods,
    ...restTableProps
  } = useTable<IPartnerUsersTableRow>({
    initialActivePage,
    initialPerPage,
    initialColumnOrderBy,
    initialColumnOrderDirection,
  });

  const {
    resetTable,
    resetActivePage,
    resetSelectedRows,
  } = resetTableMethods;

  const handleTableReset = useCallback(() => {
    resetTable();
    setSearchQuery(TablePreserverCommonInitialTableValues.searchQuery);
    setFilterValues({
      roleFilter: undefined,
    });
  }, [resetTable]);
  usePubSub(PubSubEventEnum.RESET_TABLE, handleTableReset);

  const {
    data: partnerUsers, isLoading, isFetchedAfterMount, total, refetch,
  } = useTableFetch<
    IGetPartnerUsersParams,
    TGetPartnerUsersMethod,
    GetPartnerUsersResponsePartnerUser
  >({
    request: getPartnerUsers,
    params: {
      page: activePage,
      pageLength: perPage,
      roleFilter: filterValues.roleFilter,
      searchQuery,
      orderPropertyName: PartnerUsersOrderProperty[
        upperFirst(columnOrderBy) as keyof typeof PartnerUsersOrderProperty
      ],
      isDescending: columnOrderDirection ? columnOrderDirection === 'desc' : undefined,
      ...persistentFilters,
    },
    onRequestError: handleOnRequestError,
    autoRefetch: true,
  });

  const { localization } = useTableLocalization({
    searchQuery,
    isFilterActive,
    noDataMessageKey,
    isLoading,
  });

  useTablePreserverActivePageValidator({
    activePage, resetActivePage, perPage, total, isFetchedAfterMount,
  });

  useTablePreserverEncoder<ITablePreservationConfigs>(setSearchParams as TSetSearchParams, {
    activePage,
    perPage,
    searchQuery,
    columnOrderBy,
    columnOrderDirection,
    roleFilter: filterValues.roleFilter,
  });

  return {
    partnerUsers,
    isLoading,
    total,
    refetch,
    activePage,
    perPage,
    columnOrderBy,
    columnOrderDirection,
    selectedPartnerUsers,
    localization,
    searchQuery,
    setSearchQuery,
    filterValues,
    setFilterValues,
    isFilterActive,
    numberOfActiveFilters,
    resetActivePage,
    resetSelectedRows,
    ...restTableProps,
  };
};

export default usePartnerUsersTable;
